import { useEffect, useRef, useState } from 'react';
import { HOME_WIDGET_EXIT_MS } from './widgetLayoutConstants';

type WidgetLayoutTransitionState = {
  exitingIds: Set<string>;
  enteringIds: Set<string>;
};

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * Tracks enter/exit sets for overlay + enter motion.
 * Removals are detected during render so exit overlays can read First rects
 * from the previous idle FLIP snapshot before positions are overwritten.
 */
const useWidgetLayoutTransition = (
  visibleIds: string[]
): WidgetLayoutTransitionState => {
  const previousVisibleIdsRef = useRef(visibleIds);
  const [exitingIds, setExitingIds] = useState<Set<string>>(() => new Set());
  const [enteringIds, setEnteringIds] = useState<Set<string>>(() => new Set());
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTransitionRef = useRef<{
    removedIds: string[];
    addedIds: string[];
  } | null>(null);

  if (!sameIds(previousVisibleIdsRef.current, visibleIds)) {
    const removedIds = previousVisibleIdsRef.current.filter(
      (id) => !visibleIds.includes(id)
    );
    const addedIds = visibleIds.filter(
      (id) => !previousVisibleIdsRef.current.includes(id)
    );

    previousVisibleIdsRef.current = visibleIds;
    pendingTransitionRef.current = { removedIds, addedIds };
  }

  useEffect(() => {
    const pending = pendingTransitionRef.current;
    if (!pending) {
      return undefined;
    }

    pendingTransitionRef.current = null;
    const { removedIds, addedIds } = pending;

    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    if (removedIds.length === 0 && addedIds.length === 0) {
      return undefined;
    }

    setExitingIds(new Set(removedIds));
    setEnteringIds(new Set(addedIds));

    exitTimerRef.current = setTimeout(() => {
      setExitingIds(new Set());
      setEnteringIds(new Set());
      exitTimerRef.current = null;
    }, HOME_WIDGET_EXIT_MS);

    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [visibleIds]);

  // Synchronous exit set for the render that first sees removals, so overlays
  // can mount with First rects before the FLIP effect overwrites positions.
  const pending = pendingTransitionRef.current;
  const renderExitingIds =
    pending && pending.removedIds.length > 0
      ? new Set(pending.removedIds)
      : exitingIds;
  const renderEnteringIds =
    pending && pending.addedIds.length > 0
      ? new Set(pending.addedIds)
      : enteringIds;

  return {
    exitingIds: renderExitingIds,
    enteringIds: renderEnteringIds,
  };
};

export default useWidgetLayoutTransition;
